import React, { useMemo, useState } from "react";
import {
  RiMailSendLine,
  RiMapPinLine,
  RiWhatsappFill,
  RiYoutubeLine,
} from "react-icons/ri";
import { Link } from "react-router-dom";
import {
  contactEmail,
  getWhatsAppHref,
  whatsappLabel,
} from "../config/contactChannels";
import { useApi } from "../contexts/ApiProvider";
import { useFlash } from "../contexts/FlashProvider";
import { getMarketingAttribution } from "../helpers/marketingAttribution";
import { resolveCmsAsset } from "../cms/media";
import { useCmsGlobal, useCmsPage } from "../cms/useCmsContent";

const initialForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  subject: "Demande d'information",
  organizationType: "",
  country: "",
  timeline: "",
  message: "",
  newsletterOptIn: false,
};

const requestTypes = [
  "Formation",
  "Conseil",
  "Partenariat",
  "Documents",
  "Autre demande",
];

const activityMap = {
  Formation: "formation",
  Conseil: "conseil",
  Partenariat: "partenariat",
  Documents: "documents",
};

export default function ContactPage() {
  const api = useApi();
  const flash = useFlash();
  const { content } = useCmsPage("contact");
  const { globalContent } = useCmsGlobal();
  const contact = globalContent.contact || {};
  const email = contact.email || contactEmail;
  const whatsappName = contact.whatsappLabel || whatsappLabel;
  const defaultWhatsappHref = contact.whatsapp
    ? `https://wa.me/${String(contact.whatsapp).replace(/\D/g, "")}`
    : getWhatsAppHref();
  const blocks = content.blocks || [];
  const heroBlock = blocks.find((b) => b.type === "hero");
  const formBlock = blocks.find((b) => b.type === "contact-form");
  const hero = heroBlock || content.sections?.hero || {};
  const formAside = formBlock || content.sections?.formAside || {};
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mailtoHref = useMemo(() => {
    const subject = `[Fatihoune] ${form.subject || "Demande de contact"}`;
    const body = [
      `Nom: ${form.name}`,
      `Email: ${form.email}`,
      `Société: ${form.company || "Non renseignée"}`,
      `Téléphone: ${form.phone || "Non renseigné"}`,
      `Profil: ${form.organizationType || "Non renseigné"}`,
      `Pays: ${form.country || "Non renseigné"}`,
      `Délai souhaité: ${form.timeline || "Non renseigné"}`,
      "",
      "Message:",
      form.message,
    ].join("\n");

    return `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }, [email, form]);

  const whatsappHref = useMemo(() => {
    const message = [
      "Bonjour Fatihoune, je souhaite vous contacter.",
      "",
      `Objet: ${form.subject}`,
      form.name ? `Nom: ${form.name}` : "",
      form.company ? `Organisation: ${form.company}` : "",
      form.organizationType ? `Profil: ${form.organizationType}` : "",
      form.country ? `Pays: ${form.country}` : "",
      form.timeline ? `Délai souhaité: ${form.timeline}` : "",
      form.message ? `Message: ${form.message}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    if (contact.whatsapp) {
      return `https://wa.me/${String(contact.whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(message)}`;
    }
    return getWhatsAppHref(message);
  }, [contact.whatsapp, form]);

  const contactMethods = useMemo(
    () => [
      {
        Icon: RiMailSendLine,
        label: "Email",
        value: email,
        href: `mailto:${email}`,
      },
      {
        Icon: RiWhatsappFill,
        label: whatsappName,
        value: "Message rapide",
        href: defaultWhatsappHref,
        isExternal: true,
      },
      {
        Icon: RiMapPinLine,
        label: "Siège social",
        value: contact.address || "Nahda N1 Comp Rabat, Maroc",
        href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          contact.address || "Nahda N1 Comp Rabat, Maroc"
        )}`,
      },
      {
        Icon: RiYoutubeLine,
        label: "Actualités",
        value: "YouTube Fatihoune",
        href:
          globalContent.socialLinks?.find((link) => link.label === "YouTube")?.url ||
          "https://www.youtube.com/@fatihounefatihoune3984",
      },
    ],
    [contact.address, defaultWhatsappHref, email, globalContent.socialLinks, whatsappName]
  );

  function handleChange(event) {
    const { checked, name, type, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus("");

    const response = await api.post("/api/crm/contacts", {
      fullName: form.name,
      email: form.email,
      company: form.company,
      phone: form.phone,
      subject: form.subject,
      message: form.message,
      newsletterOptIn: form.newsletterOptIn,
      source: "contact_form",
      activityType: activityMap[form.subject] || "autre",
      priority: form.timeline === "Urgent" ? "high" : "medium",
      tags: [
        form.subject,
        form.organizationType || (form.company ? "organisation" : "particulier"),
        form.country,
        form.timeline,
      ].filter(Boolean),
      metadata: {
        page: "contact",
        organizationType: form.organizationType,
        country: form.country,
        timeline: form.timeline,
        attribution: getMarketingAttribution(),
      },
    });

    setIsSubmitting(false);

    if (response.success) {
      setStatus("Votre demande est enregistrée. Notre équipe vous recontactera.");
      flash("Demande enregistrée dans le CRM.", "success");
      setForm(initialForm);
    } else {
      const message =
        response.error?.message ||
        response.message ||
        "Impossible d'enregistrer la demande pour le moment.";
      setStatus(`${message} Vous pouvez aussi nous écrire par email.`);
      flash(message, "danger");
    }
  }

  return (
    <main className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-grid">
            <div className="contact-hero-copy">
              <p className="section-kicker mb-3">
                {hero.kicker || "Contact Fatihoune"}
              </p>
              <h1>{hero.title || "Parlons de votre projet avec précision."}</h1>
              <p>
                {hero.description ||
                  "Formation, conseil, assistance ou partenariat: envoyez-nous les informations clés et nous orientons votre demande vers le bon interlocuteur."}
              </p>
              <div className="contact-hero-actions">
                <a
                  href={whatsappHref}
                  className="btn btn-whatsapp whatsapp-icon-link"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Contacter Fatihoune sur WhatsApp"
                  title="WhatsApp"
                >
                  <RiWhatsappFill aria-hidden="true" />
                  <span className="visually-hidden">WhatsApp</span>
                </a>
                <a href={`mailto:${email}`} className="btn btn-success">
                  Écrire par email
                </a>
                <Link to="/documents" className="btn btn-outline-dark">
                  Voir les documents
                </Link>
              </div>
            </div>

            <div className="contact-hero-visual" aria-label="Bureau Fatihoune">
              <img
                src={resolveCmsAsset(hero.image, "/assets/images/rabat.jpg")}
                alt="Rabat"
              />
              <div className="contact-hero-note">
                <span>{hero.noteLabel || "Base opérationnelle"}</span>
                <strong>{hero.noteValue || "Rabat, Maroc"}</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-methods-section">
        <div className="container">
          <div className="contact-methods">
            {contactMethods.map((method) => (
              <a
                key={method.label}
                className="contact-method"
                href={method.href}
                target={
                  method.isExternal || method.href.startsWith("http")
                    ? "_blank"
                    : undefined
                }
                rel={
                  method.isExternal || method.href.startsWith("http")
                    ? "noreferrer"
                    : undefined
                }
              >
                <method.Icon aria-hidden="true" />
                <span>{method.label}</span>
                <strong>{method.value}</strong>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="contact-form-section">
        <div className="container">
          <div className="contact-form-layout">
            <aside className="contact-aside">
              <p className="section-kicker mb-3">
                {formAside.kicker || "Bien orienter la demande"}
              </p>
              <h2>{formAside.title || "Quelques informations suffisent pour démarrer."}</h2>
              <p>
                {formAside.description ||
                  "Le formulaire prépare une demande structurée pour identifier le bon service, le niveau d'urgence et le contexte d'intervention."}
              </p>

              <div className="contact-request-list">
                {requestTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={form.subject === type ? "active" : ""}
                    onClick={() =>
                      setForm((current) => ({ ...current, subject: type }))
                    }
                  >
                    {type}
                  </button>
                ))}
              </div>
            </aside>

            <form className="contact-form-panel" onSubmit={handleSubmit}>
              <div className="row g-4">
                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="contact-name">
                    Nom complet
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    className="form-control"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="contact-email">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    className="form-control"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="contact-company">
                    Organisation
                  </label>
                  <input
                    id="contact-company"
                    type="text"
                    className="form-control"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="contact-phone">
                    Téléphone
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label" htmlFor="contact-organization-type">
                    Profil
                  </label>
                  <select
                    id="contact-organization-type"
                    className="form-select"
                    name="organizationType"
                    value={form.organizationType}
                    onChange={handleChange}
                  >
                    <option value="">Sélectionner</option>
                    <option value="Entreprise">Entreprise</option>
                    <option value="Institution">Institution</option>
                    <option value="Particulier">Particulier</option>
                    <option value="Partenaire">Partenaire</option>
                  </select>
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label" htmlFor="contact-country">
                    Pays
                  </label>
                  <input
                    id="contact-country"
                    type="text"
                    className="form-control"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    placeholder="Maroc, Côte d'Ivoire..."
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label" htmlFor="contact-timeline">
                    Délai souhaité
                  </label>
                  <select
                    id="contact-timeline"
                    className="form-select"
                    name="timeline"
                    value={form.timeline}
                    onChange={handleChange}
                  >
                    <option value="">Sélectionner</option>
                    <option value="Urgent">Urgent</option>
                    <option value="Sous 30 jours">Sous 30 jours</option>
                    <option value="1 à 3 mois">1 à 3 mois</option>
                    <option value="Exploration">Exploration</option>
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label" htmlFor="contact-subject">
                    Objet
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    className="form-control"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-12">
                  <label className="form-label" htmlFor="contact-message">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    className="form-control"
                    name="message"
                    rows="7"
                    value={form.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>

                <div className="col-12">
                  <label className="contact-consent" htmlFor="contact-newsletter">
                    <input
                      id="contact-newsletter"
                      type="checkbox"
                      name="newsletterOptIn"
                      checked={form.newsletterOptIn}
                      onChange={handleChange}
                    />
                    <span>
                      J'accepte de recevoir les actualités et informations de
                      Fatihoune.
                    </span>
                  </label>
                </div>

                <div className="col-12">
                  <div className="contact-form-footer">
                    <p>
                      {status ||
                        "Réponse par email après réception de votre demande."}
                    </p>
                    <div className="contact-form-actions">
                      {status && (
                        <a href={mailtoHref} className="btn btn-outline-dark btn-lg">
                          Email
                        </a>
                      )}
                      <a
                        href={whatsappHref}
                        className="btn btn-whatsapp whatsapp-icon-link"
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Contacter Fatihoune sur WhatsApp"
                        title="WhatsApp"
                      >
                        <RiWhatsappFill aria-hidden="true" />
                        <span className="visually-hidden">WhatsApp</span>
                      </a>
                      <button
                        className="btn btn-success btn-lg"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Enregistrement..." : "Envoyer la demande"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
